import React from 'react';
import styled from 'styled-components';
import { theme } from '../theme';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'destructive';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}

const StyledButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'size' && prop !== 'fullWidth',
})<ButtonProps>`
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.fontWeight.medium};
  border-radius: ${theme.borderRadius.base};
  border: none;
  cursor: pointer;
  transition: ${theme.transitions.fast};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  
  ${props => {
    switch (props.size) {
      case 'small':
        return `
          padding: ${theme.spacing.xs} ${theme.spacing.md};
          font-size: ${theme.typography.fontSize.sm};
          height: 32px;
        `;
      case 'large':
        return `
          padding: ${theme.spacing.md} ${theme.spacing.xl};
          font-size: ${theme.typography.fontSize.lg};
          height: 44px;
        `;
      default:
        return `
          padding: ${theme.spacing.sm} ${theme.spacing.lg};
          font-size: ${theme.typography.fontSize.base};
          height: 36px;
        `;
    }
  }}
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${theme.colors.primary};
          color: ${theme.colors.textInverse};
          &:hover:not(:disabled) {
            background: ${theme.colors.primaryHover};
          }
        `;
      case 'secondary':
        return `
          background: ${theme.colors.bg};
          color: ${theme.colors.textPrimary};
          border: 1px solid ${theme.colors.border};
          &:hover:not(:disabled) {
            background: ${theme.colors.bgSecondary};
          }
        `;
      case 'tertiary':
        return `
          background: transparent;
          color: ${theme.colors.textPrimary};
          &:hover:not(:disabled) {
            background: ${theme.colors.bgSecondary};
          }
        `;
      case 'destructive':
        return `
          background: ${theme.colors.error};
          color: ${theme.colors.textInverse};
          &:hover:not(:disabled) {
            background: #b91c1c;
          }
        `;
      default:
        return `
          background: ${theme.colors.primary};
          color: ${theme.colors.textInverse};
          &:hover:not(:disabled) {
            background: ${theme.colors.primaryHover};
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  ${props => props.fullWidth && 'width: 100%;'}
`;

export const Button: React.FC<ButtonProps> = ({ children, ...props }) => {
  return <StyledButton {...props}>{children}</StyledButton>;
};

export default Button;

